'use client'

import { type FormEvent, useEffect, useRef, useState } from 'react'

import { fraunces } from '@/fonts/fraunces'
import { trackCustomEvent } from '@/hooks/client/use-analytics-engine'
import { EventType } from '@/lib/analytics/events'
import { NEWSLETTER_SOURCES, type NewsletterSource } from '@/lib/newsletter/sources'
import { tw } from '@/lib/utils'

import { EditorialColumn } from './editorial-column'
import { FormCard, type SubscribeField } from './form-card'
import { SoundwaveStrip } from './soundwave-strip'
import { SuccessPanel } from './success-panel'
import { SUBSCRIBE_THEME } from './theme'
import type { SubscribeFormContent } from './types'

const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const emptyValues: Record<SubscribeField, string> = { email: '', name: '', userType: '', website: '' }

interface SubscribeFormClientProps {
  content: SubscribeFormContent
  editableProps: Record<string, unknown>
  /** Placement recorded on the contact in the newsletter admin. Defaults to the
   *  article blok — every embed except the dedicated /newsletter landing page. */
  source?: NewsletterSource
  /** Analytics position for the funnel beacons (viewed → engaged → attempted →
   *  outcome). The landing page overrides it so its conversion rate is
   *  measurable apart from the in-article embeds, which share one position. */
  trackingPosition?: string
}

export const SubscribeFormClient = ({
  content,
  editableProps,
  source = NEWSLETTER_SOURCES.subscribeFormBlok,
  trackingPosition = 'subscribe-form',
}: SubscribeFormClientProps) => {
  const tokens = SUBSCRIBE_THEME[content.theme]
  const [values, setValues] = useState(emptyValues)
  const [emailFocused, setEmailFocused] = useState(false)
  const [emailInvalid, setEmailInvalid] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [succeeded, setSucceeded] = useState(false)
  // Stamp the load time on mount only. Reading Date.now() during render/SSR is
  // forbidden under cacheComponents (a current-time read with no dynamic anchor);
  // it also only matters client-side, for the API's minimum-fill-time spam check.
  const [loadedAt, setLoadedAt] = useState(0)
  const emailInputRef = useRef<HTMLInputElement>(null)
  // Funnel beacons (viewed → engaged → attempted → outcome). The impression is
  // the section's data-track-impression; focus fires once per mount so the
  // engaged count is comparable to unique impressions.
  const focusTracked = useRef(false)
  useEffect(() => {
    setLoadedAt(Date.now())
  }, [])

  const update = (field: SubscribeField, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }))
    if (field === 'email') {
      setEmailInvalid(false)
      setErrorMessage('')
    }
  }

  const handleEmailFocus = () => {
    setEmailFocused(true)
    if (!focusTracked.current) {
      focusTracked.current = true
      trackCustomEvent('subscribe-form--focus', EventType.Click, { position: trackingPosition })
    }
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const email = values.email.trim()
    const valid = validateEmail(email)
    trackCustomEvent('subscribe-form--attempt', EventType.Click, { position: trackingPosition, valid })
    if (!valid) {
      setEmailInvalid(true)
      emailInputRef.current?.focus()
      return
    }
    setSubmitting(true)
    setErrorMessage('')
    try {
      const response = await fetch('/api/newsletter-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name: values.name.trim() || null,
          userType: values.userType || 'newsletter-subscriber',
          website: values.website,
          timeElapsed: Date.now() - loadedAt,
          source,
          // The blok can sit in any story, so the path is what makes the source
          // actionable. Read at submit time (not render) — the public URL, and
          // no `window` during SSR.
          page: window.location.pathname,
        }),
      })
      if (response.ok) {
        // Already-subscribed arrives as a 200 carrying `alreadySubscribed`, not
        // a 409 — the route stopped surfacing that status to keep it out of
        // Sentry. Branching on the status here silently stopped working when
        // that changed, so read the body instead.
        const body = (await response.json().catch(() => null)) as { alreadySubscribed?: boolean } | null
        if (body?.alreadySubscribed) {
          setErrorMessage(content.alreadySubscribedText)
          trackCustomEvent('subscribe-form--error', EventType.Click, { position: trackingPosition, status: 409 })
        } else {
          setSucceeded(true)
          trackCustomEvent('subscribe-form--success', EventType.Click, { position: trackingPosition })
        }
      } else {
        setErrorMessage(content.errorText)
        trackCustomEvent('subscribe-form--error', EventType.Click, {
          position: trackingPosition,
          status: response.status,
        })
      }
    } catch {
      setErrorMessage(content.errorText)
      trackCustomEvent('subscribe-form--error', EventType.Click, { position: trackingPosition, status: 'network' })
    } finally {
      setSubmitting(false)
    }
  }

  const handleReset = () => {
    setValues(emptyValues)
    setSucceeded(false)
    setEmailInvalid(false)
    setErrorMessage('')
    setEmailFocused(false)
  }

  const stacked = content.align === 'stacked'

  return (
    <section
      {...editableProps}
      data-track-impression
      data-event-name="subscribe-form"
      data-event-position={trackingPosition}
      data-event-origin="subscribe-form"
      className={tw(fraunces.variable, 'not-prose relative mx-auto my-8 w-full max-w-[880px]')}
    >
      <div
        className={tw(
          // Extra bottom padding relative to the top so the content clears the
          // waveform (h-10, anchored to the bottom edge) with even optical space.
          'relative overflow-hidden rounded-[28px] px-6 pt-8 pb-12 sm:px-9 sm:pt-10 sm:pb-14 md:px-12 md:pt-12 md:pb-16',
          tokens.card
        )}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-0"
          style={{ backgroundImage: `${tokens.glowA}, ${tokens.glowB}` }}
        />
        <SoundwaveStrip className={tokens.wave} />
        <div
          className={tw(
            'relative z-10',
            stacked
              ? 'mx-auto flex max-w-xl flex-col gap-7'
              : 'grid gap-9 md:grid-cols-[1.05fr_0.95fr] md:items-center md:gap-12'
          )}
        >
          <EditorialColumn content={content} tokens={tokens} />
          {succeeded ? (
            <SuccessPanel content={content} tokens={tokens} onReset={handleReset} />
          ) : (
            <FormCard
              content={content}
              tokens={tokens}
              values={values}
              emailInvalid={emailInvalid}
              emailFocused={emailFocused}
              emailInputRef={emailInputRef}
              submitting={submitting}
              errorMessage={errorMessage}
              onChange={update}
              onEmailFocus={handleEmailFocus}
              onEmailBlur={() => setEmailFocused(false)}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      </div>
    </section>
  )
}
